/**
 * useRailChoreography — decides, per card, WHO authored the change.
 *
 * The completion rail has two authors writing into ONE appointment form:
 * the person, and the system. Samer's diagnosis of the pre-Option-B build
 * was "we're mixing two systems together" — the seam between what the
 * system filled and what the human still had to do was invisible.
 *
 * Motion is how we make that seam legible, under ONE rule:
 *
 *     Motion means "the system did this."
 *
 * A row animates when the system generated or advanced it. A row that
 * ticked because the human edited a manual form section by hand appears
 * INSTANTLY — animating a person's own keystroke back at them is exactly
 * the two-systems feeling we are removing.
 *
 * ── ATTRIBUTION: A PENDING TOKEN, NOT A STOPWATCH (Codex P1, 2026-08-12)
 * The first version attributed a change to the system if it landed within
 * 600ms of a rail tap. That is wrong in the most ordinary case: tapping
 * "Pick a doctor" or "Find the patient" SCROLLS TO AN INLINE SECTION, and
 * a human takes well over 600ms to actually choose. The answer then got
 * classified human-authored and every intended check/open animation was
 * suppressed — the exact opposite of the intent.
 *
 * A tap now opens a PENDING ACTION that stays open until the card it
 * belongs to actually changes, however long the human takes. No clock.
 * A change with no pending action can only have come from the manual
 * sections, so it stays instant.
 *
 * ── WHY STATE ADJUSTED DURING RENDER, NOT AN EFFECT
 * Deriving in `useEffect` runs AFTER paint, so a newly-generated card's
 * first paint misses its own entrance — rows pop into place and only then
 * animate (measured: shell rendered at animation-duration 0s, then flipped
 * to 0.22s). Deriving from refs during render fixed the timing but violates
 * `react-hooks/refs` + `react-hooks/purity` (11 lint errors, would fail CI)
 * and can record an uncommitted card set if a concurrent render is
 * interrupted.
 *
 * The pattern below is React's documented "adjusting state when props
 * change": compare against previous state DURING render and call setState
 * on this same component. React re-runs the component immediately without
 * painting the intermediate result — so the first painted frame already
 * carries the entrance, with no refs and no purity violation.
 */

import { useCallback, useState } from 'react';
import type { CompletionCard, CompletionCardKind } from './composition-engine';

/** Who caused a card to reach its current state. */
export type CardAuthor = 'system' | 'human';

export interface RailChoreography {
  /** Author of each card's current state, keyed by card.key. */
  authorOf: Record<string, CardAuthor>;
  /** Cards that entered on this render (never seen before). */
  entering: ReadonlySet<string>;
  /** Cards that just flipped to `done` — the check should land. */
  justResolved: ReadonlySet<string>;
  /** The card that just became the open ask, if it changed. */
  justOpened: string | null;
  /** True on the render where a brand-new draft first appears. */
  isFreshDraft: boolean;
  /** Call from the rail's tap handler so the next change is attributed. */
  noteRailTap: (kind: CompletionCardKind) => void;
}

/** Stable signature of the card set — identity + state, in order. */
export function cardsSignature(cards: CompletionCard[]): string {
  return cards.map((c) => `${c.key}:${c.state}`).join('|');
}

/**
 * Diff two card lists into the sets the motion layer needs.
 * Pure — exported so the mapping is testable without mounting.
 */
export function diffCards(
  prev: CompletionCard[] | null,
  next: CompletionCard[],
): {
  entering: Set<string>;
  justResolved: Set<string>;
  justOpened: string | null;
} {
  const entering = new Set<string>();
  const justResolved = new Set<string>();
  let justOpened: string | null = null;

  const prevByKey = new Map((prev ?? []).map((c) => [c.key, c]));

  for (const card of next) {
    const before = prevByKey.get(card.key);

    // Never seen → it entered.
    if (!before) {
      entering.add(card.key);
      // A card that arrives already open is also a new ask, but on a
      // fresh draft the cascade owns the motion — don't double-animate.
      if (card.state === 'open' && prev !== null) justOpened = card.key;
      continue;
    }

    if (before.state !== 'done' && card.state === 'done') {
      justResolved.add(card.key);
    }
    if (before.state !== 'open' && card.state === 'open') {
      justOpened = card.key;
    }
  }

  return { entering, justResolved, justOpened };
}

type Derived = Omit<RailChoreography, 'noteRailTap'>;

const EMPTY: Derived = {
  authorOf: {},
  entering: new Set(),
  justResolved: new Set(),
  justOpened: null,
  isFreshDraft: true,
};

/**
 * Pure core — exported so attribution can be tested directly, without a
 * component and without fake timers.
 */
export function deriveChoreography(
  prevCards: CompletionCard[] | null,
  cards: CompletionCard[],
  isFreshDraft: boolean,
  advancedByRail: boolean,
): Derived {
  const { entering, justResolved, justOpened } = diffCards(
    isFreshDraft ? null : prevCards,
    cards,
  );

  // A fresh draft is wholly system-authored — the system just generated
  // the entire checklist. Otherwise a change counts as system-advanced
  // only if a rail tap is still pending against it.
  const authorOf: Record<string, CardAuthor> = {};
  for (const card of cards) {
    if (isFreshDraft) {
      authorOf[card.key] = 'system';
    } else if (justResolved.has(card.key) || card.key === justOpened) {
      authorOf[card.key] = advancedByRail ? 'system' : 'human';
    } else {
      authorOf[card.key] = 'system';
    }
  }

  const systemDriven = isFreshDraft || advancedByRail;
  return {
    authorOf,
    // On a human-authored change nothing animates at all.
    entering: systemDriven ? entering : new Set<string>(),
    justResolved: systemDriven ? justResolved : new Set<string>(),
    justOpened: systemDriven ? justOpened : null,
    isFreshDraft,
  };
}

/**
 * A rail tap is spent by the FIRST composition change that follows it —
 * whatever changed.
 *
 * An earlier version only consumed the tap when the TARGETED card entered
 * `justResolved` or `justOpened`. Codex found two ordinary paths where the
 * target does neither, so the tap was never consumed and leaked forever:
 *
 *   - completing `register-patient` makes that card DISAPPEAR rather than
 *     resolve — it is absent from the next composition, not `done`;
 *   - editing an already-`done` card (tap Edit on Patient, pick a different
 *     patient) leaves the card `done` before and after — no state change.
 *
 * A leaked tap is worse than a missed one: every subsequent MANUAL edit is
 * then attributed to the system and animated, which is precisely the rule
 * this hook exists to enforce, inverted.
 *
 * "First change spends it" cannot leak. Its failure mode is the benign
 * direction — an unrelated change landing first spends the tap early, so ONE
 * system-authored change renders as human (no animation). Under-animating
 * once beats mis-attributing everything after.
 *
 * Implemented at the consume site in useRailChoreography below.
 */

interface Snapshot {
  sig: string | null;
  cards: CompletionCard[] | null;
  draftId: string | undefined;
}

export function useRailChoreography(
  cards: CompletionCard[],
  /** Draft identity — a new value means a brand-new request. */
  draftId: string | undefined,
): RailChoreography {
  const [snapshot, setSnapshot] = useState<Snapshot>({
    sig: null,
    cards: null,
    draftId: undefined,
  });
  const [derived, setDerived] = useState<Derived>(EMPTY);
  /**
   * The card kind a rail tap is currently waiting on. Survives as long as
   * the human takes — an inline picker can sit open for many seconds.
   */
  const [pendingTap, setPendingTap] = useState<CompletionCardKind | null>(null);

  const noteRailTap = useCallback((kind: CompletionCardKind) => {
    setPendingTap(kind);
  }, []);

  // Adjust state during render (React's documented pattern) so a card's
  // FIRST painted frame already carries its entrance. Guarded on the card
  // signature, so the rail's per-keystroke re-renders — resolution derives
  // from react-hook-form `watch` — recompute nothing.
  const sig = cardsSignature(cards);
  if (sig !== snapshot.sig || draftId !== snapshot.draftId) {
    const isFreshDraft = draftId !== snapshot.draftId;
    const next = deriveChoreography(
      snapshot.cards,
      cards,
      isFreshDraft,
      pendingTap !== null,
    );

    setSnapshot({ sig, cards, draftId });
    setDerived(next);

    // Spend the tap on THIS change, whatever changed — see
    // TAP_IS_SPENT_BY_ANY_CHANGE. Waiting for the tapped card specifically to
    // resolve leaks the tap forever on the two paths where it never does
    // (register-patient disappears; editing a done card leaves it done), and
    // a leaked tap mis-attributes every later manual edit to the system.
    // A brand-new request also invalidates anything left from the old one.
    if (pendingTap !== null) setPendingTap(null);

    return { ...next, noteRailTap };
  }

  return { ...derived, noteRailTap };
}
